/**
 * Where to send a user after they sign in.
 *
 * WHY THIS IS ITS OWN FILE WITH ITS OWN TESTS
 * -------------------------------------------
 * The landing page's quick links point at routes behind a login, so the path a
 * user wanted travels through the URL: `/login?next=/portal/fees`. Anything that
 * reads a redirect target out of a URL and then navigates to it is an open
 * redirect waiting to happen — `?next=https://xpsims-fees.example/login` renders
 * a convincing phishing page that the user arrived at by clicking a link on the
 * university's own domain, with the university's own domain in their history.
 *
 * The rule is therefore not "sanitise it" but "only ever accept a path": one
 * leading slash, no scheme, no host. Everything else falls back to the user's
 * own dashboard, which is never wrong, only less specific.
 *
 * WHOSE PATH IS IT, THOUGH
 * ------------------------
 * "Is this a path on this site" turned out not to be the whole question. A
 * signed-in user who signs out is bounced here by `RequireAuth`, which writes
 * the page they were on into `?next=`. That parameter then sits in the URL
 * while the NEXT person signs in at the same screen — and a lecturer following
 * an administrator at a shared desk was sent straight to the student record the
 * administrator had open.
 *
 * Nothing about that path was unsafe by the test above; it was simply not
 * theirs. So the destination must also belong to the audience of whoever
 * actually signed in, and `RequireAuth` stops writing the parameter at all when
 * the user is leaving on purpose.
 */

/**
 * The one screen an account that owes a password change may reach.
 *
 * Top-level rather than inside a shell: the shells render navigation to the
 * whole platform, and offering a menu of places the gateway will refuse is
 * worse than offering none.
 */
export const CHANGE_PASSWORD_PATH = "/change-password";

/** Where each kind of user belongs when there is no better answer. */
export const HOME_BY_TYPE = {
    staff: "/staff",
    student: "/portal",
    guardian: "/guardian",
} as const;

/**
 * Decides whether a candidate redirect target is a path on this site.
 *
 * @param candidate - The untrusted value, typically from `?next=`
 * @returns True when it is safe to navigate to
 */
export function isInternalPath(candidate: string | null | undefined): candidate is string {
    if (!candidate || candidate[0] !== "/") {
        return false;
    }

    // `//evil.com` and `/\evil.com` are protocol-relative URLs: the browser
    // treats both as absolute and leaves the site. They pass a naive
    // "starts with a slash" check, which is exactly why that check is not enough.
    if (candidate[1] === "/" || candidate[1] === "\\") {
        return false;
    }

    // A newline or tab can split a header or slip past a downstream parser that
    // is more forgiving than this one.
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(candidate)) {
        return false;
    }

    return true;
}

/**
 * Whether a path lies inside this kind of user's own part of the site.
 *
 * Segment-aware: `/staffroom` must not count as being under `/staff`, or a
 * route added later under a name that merely starts the same way would inherit
 * a redirect nobody intended.
 *
 * @param candidate - A path that has already passed {@link isInternalPath}
 * @param userType - The signed-in user's type
 * @returns True when the path belongs to that audience
 */
function belongsTo(candidate: string, userType: keyof typeof HOME_BY_TYPE): boolean {
    const home = HOME_BY_TYPE[userType];
    // `/staff?tab=roles` is the dashboard with a query on it, and comparing the
    // whole string would send that user to a bare `/staff` instead.
    const path = candidate.split(/[?#]/)[0];

    return path === home || path.startsWith(`${home}/`);
}

/**
 * Resolves the post-login destination.
 *
 * Two tests, not one: the path must be on this site, and it must be in the
 * signed-in user's own area. The second is what stops one person's session
 * handing its last page to the next person to sign in at the same screen — see
 * the note at the top of this file.
 *
 * @param next - The `?next=` value, untrusted
 * @param fromState - A path carried in router state by `RequireAuth`
 * @param userType - The signed-in user's type, used for the fallback
 * @returns A path on this site, belonging to this user
 */
export function resolveNextPath(
    next: string | null | undefined,
    fromState: string | null | undefined,
    userType: keyof typeof HOME_BY_TYPE
): string {
    if (isInternalPath(next) && belongsTo(next, userType)) {
        return next;
    }

    if (isInternalPath(fromState) && belongsTo(fromState, userType)) {
        return fromState;
    }

    return HOME_BY_TYPE[userType];
}

/**
 * Builds the sign-in link for a destination behind a login.
 *
 * Used by the landing page's quick links, so that following one signs in and
 * arrives at the thing that was clicked rather than at a dashboard.
 *
 * @param destination - The path the user was trying to reach
 * @returns A `/login` URL carrying that path
 */
export function loginHref(destination: string): string {
    return `/login?next=${encodeURIComponent(destination)}`;
}
